import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from 'react'
import axios from 'axios'
import authApi from '../api/auth/AuthApi'
import { AuthHelper } from './AuthHelper'
import {
    AuthContext,
    type AuthContextValue,
    type AuthSessionUser,
} from './AuthContext'

type AuthProviderProps = {
    children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
    const [user, setUser] = useState<AuthSessionUser | null>(null)
    const [loading, setLoading] = useState<boolean>(true)
    const [error, setError] = useState<string | undefined>()
    const refreshSeq = useRef(0)

    const refresh = useCallback(async () => {
        const runId = ++refreshSeq.current
        try {
            setLoading(true)
            setError(undefined)
            const res = await authApi.getMe()
            if (runId !== refreshSeq.current) return
            setUser(res.user)
        } catch (err: any) {
            if (runId !== refreshSeq.current) return
            setUser(null)
            if (axios.isAxiosError(err) && err.response?.status === 401) {
                AuthHelper.removeTenantOverrideId()
                setError(undefined)
            } else if (err instanceof Error) {
                setError(err.message)
            } else {
                setError('Unable to fetch session')
            }
        } finally {
            if (runId === refreshSeq.current) {
                setLoading(false)
            }
        }
    }, [])

    useEffect(() => {
        refresh()
    }, [refresh])

    const handleLoginWithPassword = useCallback(
        async (email: string, password: string, recaptchaToken?: string) => {
            await authApi.loginWithPassword(email, password, recaptchaToken)
            await refresh()
        },
        [refresh],
    )

    const handleMagicLogin = useCallback(
        async (token: string) => {
            await authApi.magicLogin(token)
            await refresh()
        },
        [refresh],
    )

    const handleRequestMagicLink = useCallback(
        async (email: string, recaptchaToken?: string, target?: string) => {
            await authApi.requestMagicLink(email, recaptchaToken, target)
        },
        [],
    )

    const handleLogout = useCallback(async () => {
        try {
            await authApi.logout()
        } finally {
            setUser(null)
            setError(undefined)
        }
    }, [])

    const value = useMemo<AuthContextValue>(
        () => ({
            user,
            loading,
            error,
            refresh,
            loginWithPassword: handleLoginWithPassword,
            requestMagicLink: handleRequestMagicLink,
            magicLogin: handleMagicLogin,
            logout: handleLogout,
        }),
        [
            user,
            loading,
            error,
            refresh,
            handleLoginWithPassword,
            handleRequestMagicLink,
            handleMagicLogin,
            handleLogout,
        ],
    )
    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
